import { useBasket } from '@view/view_create_new_message/hooks/use-basket';
import { useCallback, useMemo } from 'react';
import { useCreateNewMessage } from '../../hooks/use-create-new-message';

interface SaveParticipantsData {
	access: string[];
}

function normalizeAccess(access: string[], patientParticipantIds: string[]): string[] {
	const alwaysIncluded = new Set(['me', ...patientParticipantIds]);
	return ['me', ...patientParticipantIds, ...access.filter((value) => !alwaysIncluded.has(value))];
}

export function useSaveParticipants() {
	const { flowSettings, updateFlowSettings } = useCreateNewMessage();
	const { basket, selectParticipants, deselectParticipants } = useBasket();

	const patientParticipantIds = useMemo(
		() => (basket?.Participants ?? []).filter((p) => p.Type === 'Patient').map((p) => String(p.Id)),
		[basket],
	);

	const access = useMemo(
		() =>
			flowSettings.flowState === 'accessible'
				? normalizeAccess(flowSettings.value.access, patientParticipantIds)
				: ['me', ...patientParticipantIds],
		[flowSettings, patientParticipantIds],
	);

	const syncBasketParticipants = useCallback(
		async (access: string[]) => {
			if (basket == null || basket.Participants.length === 0) {
				return;
			}

			const selectedAccess = new Set(access);
			const selectedParticipantIds: number[] = [];
			const deselectedParticipantIds: number[] = [];

			for (const participant of basket.Participants) {
				const isSelectedByParticipantId = selectedAccess.has(String(participant.Id));

				if (isSelectedByParticipantId) {
					selectedParticipantIds.push(participant.Id);
				} else {
					deselectedParticipantIds.push(participant.Id);
				}
			}

			await Promise.all([selectParticipants(selectedParticipantIds), deselectParticipants(deselectedParticipantIds)]);
		},
		[basket, deselectParticipants, selectParticipants],
	);

	const save = useCallback(
		async (data: SaveParticipantsData) => {
			const access = normalizeAccess(data.access, patientParticipantIds);
			await syncBasketParticipants(access);

			updateFlowSettings({ access });
		},
		[patientParticipantIds, syncBasketParticipants, updateFlowSettings],
	);

	return { access, patientParticipantIds, save };
}
